/**
 * Browser-session memory of data a principal was just authorized to read,
 * bound to that principal. An entry is served only to the same confirmed
 * identity, never without one, and every memory is wiped synchronously when
 * the confirmed identity changes (sign-out, account switch). Callers use it
 * only as a placeholder while the live, access-checked query loads, and
 * forget an entry as soon as that query refuses it.
 */
export type IdentityScopedSessionMemory<V> = {
  get: (identity: string | null, key: string) => V | null;
  set: (identity: string | null, key: string, value: V) => void;
  forget: (key: string) => void;
  clear: () => void;
};

const memories = new Set<{ clear: () => void }>();

export function createIdentityScopedSessionMemory<V>(
  limit: number
): IdentityScopedSessionMemory<V> {
  const entries = new Map<string, { identity: string; value: V }>();
  const memory: IdentityScopedSessionMemory<V> = {
    get(identity, key) {
      if (typeof window === 'undefined' || !identity) return null;
      const entry = entries.get(key);
      return entry && entry.identity === identity ? entry.value : null;
    },
    set(identity, key, value) {
      if (typeof window === 'undefined' || !identity) return;
      entries.delete(key);
      entries.set(key, { identity, value });
      if (entries.size > limit) {
        const oldest = entries.keys().next().value;
        if (oldest !== undefined) entries.delete(oldest);
      }
    },
    forget(key) {
      entries.delete(key);
    },
    clear() {
      entries.clear();
    },
  };
  memories.add(memory);
  return memory;
}

/** Called by the Convex auth lifecycle on every confirmed-identity change. */
export function clearIdentityScopedSessionMemories(): void {
  for (const memory of memories) memory.clear();
}
